import { API_BASE } from '../../config/apiBase';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import { 
  ArrowLeft, ArrowRight, CheckCircle, XCircle,
  HelpCircle, Clock, AlertTriangle, Trophy
} from 'lucide-react';
import { useGenericTimeTracking } from '../../hooks/useTimeTracking';

const API = API_BASE;

export default function TrainingQuiz() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Track time spent on the quiz (pauses when tab hidden, no double counting)
  useGenericTimeTracking('quiz', quizId, !submitted);

  useEffect(() => {
    fetchQuiz();
  }, [quizId]);

  const fetchQuiz = async () => {
    try {
      const response = await axios.get(`${API}/training/quizzes/${quizId}`);
      setQuiz(response.data);
      setAnswers(new Array(response.data.questions?.length || 0).fill(-1));
    } catch (error) {
      console.error('Error fetching quiz:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionIndex, answerIndex) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = answerIndex;
    setAnswers(newAnswers);
  };

  const handleSubmit = async () => {
    // Check if all questions answered
    if (answers.some(a => a === -1)) {
      alert(lang === 'es' ? 'Por favor responde todas las preguntas' : 'Please answer all questions');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/training/quizzes/${quizId}/submit`, {
        answers
      });
      setResult(response.data);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting quiz:', error);
      alert(error.response?.data?.detail || 'Error submitting quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const lang = user?.training_language || 'en';
  const t = (en, es) => lang === 'es' ? es : en;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{t('Quiz not found', 'Quiz no encontrado')}</p>
        <Button onClick={() => navigate('/training')} className="mt-4">
          {t('Back to Training', 'Volver a Capacitación')}
        </Button>
      </div>
    );
  }

  // Already passed
  if (quiz.passed && !submitted) {
    return (
      <div className="max-w-2xl mx-auto p-6 space-y-6">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('Back', 'Volver')}
        </Button>
        
        <Card className="border-green-500 bg-green-50">
          <CardContent className="p-8 text-center">
            <Trophy className="h-16 w-16 mx-auto text-green-500 mb-4" />
            <h2 className="text-2xl font-bold text-green-700">
              {t('Quiz Already Passed!', '¡Quiz ya Aprobado!')}
            </h2>
            <p className="text-green-600 mt-2">
              {t('You have already passed this quiz.', 'Ya has aprobado este quiz.')}
            </p>
            <Button onClick={() => navigate(-1)} className="mt-6">
              {t('Continue Training', 'Continuar Capacitación')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No more attempts
  if (!quiz.can_attempt && !submitted) {
    return (
      <div className="max-w-2xl mx-auto p-6 space-y-6">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('Back', 'Volver')}
        </Button>
        
        <Card className="border-red-500 bg-red-50">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-16 w-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-2xl font-bold text-red-700">
              {t('No More Attempts', 'Sin Más Intentos')}
            </h2>
            <p className="text-red-600 mt-2">
              {t(
                `You have used all ${quiz.max_attempts} attempts for this quiz.`,
                `Has usado los ${quiz.max_attempts} intentos para este quiz.`
              )}
            </p>
            <Button onClick={() => navigate(-1)} className="mt-6" variant="outline">
              {t('Back to Training', 'Volver a Capacitación')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show results
  if (submitted && result) {
    return (
      <div className="max-w-2xl mx-auto p-6 space-y-6">
        <Card className={result.passed ? 'border-green-500' : 'border-amber-500'}>
          <CardContent className="p-8 text-center">
            {result.passed ? (
              <>
                <CheckCircle className="h-16 w-16 mx-auto text-green-500 mb-4" />
                <h2 className="text-2xl font-bold text-green-700">
                  {t('Congratulations!', '¡Felicitaciones!')}
                </h2>
                <p className="text-green-600 mt-2">
                  {t('You passed the quiz!', '¡Aprobaste el quiz!')}
                </p>
              </>
            ) : (
              <>
                <XCircle className="h-16 w-16 mx-auto text-amber-500 mb-4" />
                <h2 className="text-2xl font-bold text-amber-700">
                  {t('Try Again', 'Intenta de Nuevo')}
                </h2>
                <p className="text-amber-600 mt-2">
                  {t('You need 80% to pass. Review the material and try again.', 
                     'Necesitas 80% para aprobar. Revisa el material e intenta de nuevo.')}
                </p>
              </>
            )}
            
            <div className="mt-6 p-4 bg-white/50 rounded-lg">
              <div className="text-4xl font-bold">
                {result.score.toFixed(0)}%
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {result.correct_answers} / {result.total_questions} {t('correct', 'correctas')}
              </p>
              {result.remaining_attempts > 0 && !result.passed && (
                <p className="text-sm mt-2">
                  {t('Attempts remaining:', 'Intentos restantes:')} {result.remaining_attempts}
                </p>
              )}
            </div>

            {/* Show feedback for each question */}
            {result.feedback && (
              <div className="mt-6 text-left space-y-3">
                <h3 className="font-semibold">{t('Review:', 'Revisión:')}</h3>
                {result.feedback.map((fb, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-lg ${
                      fb.correct ? 'bg-green-100' : 'bg-red-100'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {fb.correct ? (
                        <CheckCircle className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                      )}
                      <div>
                        <p className="font-medium text-sm">
                          {lang === 'es' ? fb.question_es : fb.question_en}
                        </p>
                        {!fb.correct && fb.explanation && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {lang === 'es' ? fb.explanation_es : fb.explanation_en}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 flex gap-4 justify-center">
              {!result.passed && result.remaining_attempts > 0 && (
                <Button 
                  onClick={() => {
                    setSubmitted(false);
                    setResult(null);
                    setAnswers(new Array(quiz.questions?.length || 0).fill(-1));
                    setCurrentQuestion(0);
                    fetchQuiz();
                  }}
                >
                  {t('Try Again', 'Intentar de Nuevo')}
                </Button>
              )}
              <Button 
                variant={result.passed ? "default" : "outline"}
                onClick={() => navigate(-1)}
              >
                {t('Continue', 'Continuar')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Quiz taking UI
  const questions = quiz.questions || [];
  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const answeredCount = answers.filter(a => a !== -1).length;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('Exit Quiz', 'Salir del Quiz')}
        </Button>
        <div className="flex items-center gap-4">
          <Badge variant="outline">
            {quiz.user_attempts || 0} / {quiz.max_attempts} {t('attempts', 'intentos')}
          </Badge>
          <Badge variant="secondary">
            {answeredCount} / {questions.length} {t('answered', 'respondidas')}
          </Badge>
        </div>
      </div>

      {/* Quiz Title */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <HelpCircle className="h-6 w-6 text-primary" />
            <CardTitle>{lang === 'es' ? quiz.title_es : quiz.title_en}</CardTitle>
          </div>
          <Progress value={progress} className="h-2 mt-4" />
          <p className="text-sm text-muted-foreground mt-2">
            {t('Question', 'Pregunta')} {currentQuestion + 1} {t('of', 'de')} {questions.length}
          </p>
        </CardHeader>
      </Card>

      {/* Question */}
      {question && (
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-6">
              {lang === 'es' ? question.question_es : question.question_en}
            </h3>

            <RadioGroup
              value={answers[currentQuestion]?.toString()}
              onValueChange={(value) => handleAnswer(currentQuestion, parseInt(value))}
              className="space-y-3"
            >
              {(lang === 'es' ? question.options_es : question.options_en).map((option, idx) => (
                <div
                  key={idx}
                  className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                    answers[currentQuestion] === idx 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => handleAnswer(currentQuestion, idx)}
                >
                  <RadioGroupItem value={idx.toString()} id={`option-${idx}`} />
                  <Label 
                    htmlFor={`option-${idx}`} 
                    className="flex-1 cursor-pointer"
                  >
                    {option}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
          disabled={currentQuestion === 0}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('Previous', 'Anterior')}
        </Button>

        <div className="flex gap-2">
          {questions.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentQuestion(idx)}
              className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                idx === currentQuestion
                  ? 'bg-primary text-primary-foreground'
                  : answers[idx] !== -1
                    ? 'bg-green-500 text-white'
                    : 'bg-muted hover:bg-muted/80'
              }`}
            >
              {idx + 1}
            </button>
          ))}
        </div>

        {currentQuestion < questions.length - 1 ? (
          <Button
            onClick={() => setCurrentQuestion(prev => Math.min(questions.length - 1, prev + 1))}
          >
            {t('Next', 'Siguiente')}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={submitting || answers.some(a => a === -1)}
            className="bg-green-600 hover:bg-green-700"
          >
            {submitting ? (
              <>{t('Submitting...', 'Enviando...')}</>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                {t('Submit Quiz', 'Enviar Quiz')}
              </>
            )}
          </Button>
        )}
      </div>

      {/* Quick navigation hint */}
      <p className="text-center text-sm text-muted-foreground">
        {t('Click on a number to jump to that question', 'Haz clic en un número para ir a esa pregunta')}
      </p>
    </div>
  );
}
